import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { optionalAsset } from '../frontend/services/assetResolverService';
import { BracketFormat } from '../types/eventBracketTypes';
import { TResultStandingAggregationList, TResultStandingKeystonesRankings } from '../types/eventStandingsTypes';
import {
  EventMode,
  EventType,
  ICollectionWithEventBracketSummary,
  IEventCollectionSummary,
  IEventHubCollectionEventSummary,
  IEventHubCollectionSummary,
  IEventSummary,
  IEventWithBracketsSummary,
  LiveEventMode,
  TLatestEventData,
  TLatestEventDataSuccess
} from '../types/eventTypes';

export const getEventTypePriority = (type: EventType) => {
  switch (type) {
    case EventType.MDI:
      return 1;
    case EventType.AWC:
      return 2;
    case EventType.VARIETY_SHOW:
      return 3;
    case EventType.Legacy:
      return 4;
    default:
      return 5;
  }
};

// First come started events, then with active registration, then waiting for registration, then waiting for start, then completed, then unknown
export const getEventModePriority = (mode: EventMode) => {
  return [
    EventMode.STARTED,
    EventMode.ACTIVE,
    EventMode.WAITING_REGISTRATION,
    EventMode.WAITING_EVENT_START,
    EventMode.COMPLETED,
    EventMode.UNKNOWN
  ].indexOf(mode);
};

export function sortEvents(
  a: IEventWithBracketsSummary | IEventHubCollectionEventSummary | IEventSummary,
  b: IEventWithBracketsSummary | IEventHubCollectionEventSummary | IEventSummary
) {
  const aModePriority = getEventModePriority(a.mode);
  const bModePriority = getEventModePriority(b.mode);

  if (aModePriority !== bModePriority) {
    return aModePriority - bModePriority;
  }

  const aTypePriority = getEventTypePriority(a.type);
  const bTypePriority = getEventTypePriority(b.type);

  if (aTypePriority !== bTypePriority) {
    return aTypePriority - bTypePriority;
  }

  if (a.mode === EventMode.COMPLETED && b.mode === EventMode.COMPLETED) {
    if (a.eventEndsAt && b.eventEndsAt) {
      return moment(b.eventEndsAt).diff(moment(a.eventEndsAt));
    }

    return a.title.localeCompare(b.title);
  }

  if (a.mode === EventMode.ACTIVE && b.mode === EventMode.ACTIVE) {
    if (a.registrationEndsAt && b.registrationEndsAt) {
      const diffInDays = moment(a.registrationEndsAt).diff(moment(b.registrationEndsAt), 'days');
      if (diffInDays !== 0) {
        return diffInDays;
      }
    }

    return a.title.localeCompare(b.title);
  }

  return a.title.localeCompare(b.title);
}

function compareCollectionsForEventSort(
  a: ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary,
  b: ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary
) {
  const aEvent = a.events[0];
  const bEvent = b.events[0];

  if (!aEvent && !bEvent) {return 0;}
  if (!aEvent) {return 1;}
  if (!bEvent) {return -1;}

  const aModePriority = getEventModePriority(aEvent.mode);
  const bModePriority = getEventModePriority(bEvent.mode);

  // If both events are COMPLETED, sort by event end date (descending)
  if (aEvent.mode === EventMode.COMPLETED && bEvent.mode === EventMode.COMPLETED) {
    const aEnd = aEvent.eventEndsAt ? new Date(aEvent.eventEndsAt).getTime() : 0;
    const bEnd = bEvent.eventEndsAt ? new Date(bEvent.eventEndsAt).getTime() : 0;

    if (aEnd !== bEnd) {
      // Most recent ends first
      return bEnd - aEnd;
    }
  }

  if (aModePriority !== bModePriority) {
    return aModePriority - bModePriority;
  }

  const aTypePriority = getEventTypePriority(aEvent.type);
  const bTypePriority = getEventTypePriority(bEvent.type);

  return aTypePriority - bTypePriority;
}

export function sortCollections<
  T extends ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary
>(collections: T[]): T[] {
  return [...collections].sort(compareCollectionsForEventSort);
}

class IdGenerator {
  private static newIdPrefix = 'new_';

  constructor(private prefix: string) {
    this.prefix = prefix;
  }

  generateNewId() {
    return `${IdGenerator.newIdPrefix}_${this.prefix}_${uuidv4()}`;
  }

  isNewId(id: string | number) {
    return typeof id === 'string' && id.startsWith(IdGenerator.newIdPrefix);
  }

  getElementId(id: string | number) {
    return `${IdGenerator.newIdPrefix}_${this.prefix}-${id}`;
  }

  static isNewId(id: string | number) {
    return typeof id === 'string' && id.startsWith(IdGenerator.newIdPrefix);
  }
}

export const registrationStepIdGenerator = new IdGenerator('registrationStep');
export const eventContentIdGenerator = new IdGenerator('eventContent');

export class LiveEventParamKeyGenerator {
  private static prefix = 'hub_live_event';

  private static getParam(mode: LiveEventMode, key: string) {
    return `${this.prefix}_${mode}_${key}`;
  }

  static getRankingsLimit(mode: LiveEventMode) {
    return this.getParam(mode, 'rankings_limit');
  }

  static getCoverageInfo(mode: LiveEventMode) {
    return this.getParam(mode, 'coverage_info');
  }

  static getEventSlug(mode: LiveEventMode) {
    return this.getParam(mode, 'slug');
  }

  static getBracketSlug(mode: LiveEventMode) {
    return this.getParam(mode, 'bracket_slug');
  }

  static getVideoConfigKey(mode: LiveEventMode) {
    return this.getParam(mode, 'video_config');
  }

  static getMastheadLayoutKey(mode: LiveEventMode) {
    return this.getParam(mode, 'masthead_layout');
  }
}

export const getEventBackgroundUrl = (event?: Pick<IEventSummary, 'backgroundImage'> | null): string | null => {
  if (!event) {
    return null;
  }
  const img = event.backgroundImage;
  return img ? img.storageItem?.url || optionalAsset(img.path) : null;
};

export const isLatestEventDataSuccess = (data: TLatestEventData): data is TLatestEventDataSuccess => {
  return !('error' in data);
};

export const isEventStandingsRankings = (
  data: TResultStandingKeystonesRankings | TResultStandingAggregationList
): data is TResultStandingKeystonesRankings => {
  return 'rankings' in data;
};

export const isKeystoneRankingsBracket = (bracket: { format: BracketFormat }): boolean => {
  return [BracketFormat.THE_GREAT_PUSH, BracketFormat.TIME_TRIALS].includes(bracket.format);
};
